import { useCallback, useState } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { Download } from '@emotion-icons/material/Download'
import { ButtonWithIcon } from 'src/components/button/button-with-icon'
import { InsightDownloadModal } from 'src/views/insight-download/modal'
import { useResetReportSettings } from 'src/views/insight-download/use-reset-report-settings'
import { logEvent } from 'src/components/analytics'
import { useDownloadLimit } from 'src/views/insight-download/use-limit-downloads'
import { Badge, Typography } from '@mui/material'
import { DarkTooltip } from 'src/components/tooltip/dark-tooltip'

import * as reportInsight from 'src/atoms/report-insight'
import * as userAtoms from 'src/atoms/user'
import { Colors } from 'src/components/theme'

type InsightDownloadProps = {
  fileName: string
  style?: React.CSSProperties
}

export const InsightDownload = (props: InsightDownloadProps) => {
  const { fileName, style } = props
  const [open, setOpen] = useState(false)
  const setTitle = useSetRecoilState(reportInsight.reportTitle)
  const resetReportSettings = useResetReportSettings()
  const { limitReached, downloadsLeft } = useDownloadLimit()
  const isTrial = useRecoilValue(userAtoms.isTrial)

  const onOpen = useCallback(() => {
    if (limitReached) {
      return
    }
    setTitle((prev) => ({
      ...prev,
      text: fileName,
    }))
    logEvent('insight-download')
    setOpen(true)
  }, [limitReached, fileName])

  const onClose = () => {
    resetReportSettings()
    setOpen(false)
  }

  return (
    <>
      <DarkTooltip
        title={
          isTrial
            ? limitReached
              ? 'You have reached the download limit'
              : `You have ${downloadsLeft} report downloads remaining`
            : null
        }>
        <Badge
          color="secondary"
          badgeContent={
            isTrial && downloadsLeft > 0 ? (
              <Typography variant="text1" color="white">
                {downloadsLeft}
              </Typography>
            ) : null
          }>
          <ButtonWithIcon disabled={limitReached} style={style} inverted onClick={onOpen}>
            <Download size={16} color={Colors.textWhite} />
            Download Report
          </ButtonWithIcon>
        </Badge>
      </DarkTooltip>
      <InsightDownloadModal open={open} onClose={onClose} />
    </>
  )
}
